import { useState, useEffect, useMemo } from "react";
import { useFreezeApplication } from "@/hooks/useFreezeApplication";
import { useTickets } from "@/hooks/useTickets";
import { useGetClosedTicketsQuery } from "@/queries/useGetClosedTicketsQuery";
import type { Ticket, PackageTicket } from "@/@types/tickets.types";
import { useUpdateFreezeApplicationMutation } from "@/mutations/useUpdateFreezeApplicationMutation";
import { useAuth } from "@/contexts/AuthContext";
import type { FreezeApplication as FreezeApplicationType } from "@/http/freezeApplicationsApi";
import { uploadAttachment } from "@/http/freezeApplicationsApi";
import { formatDateWithYear, formatDateShort } from "@/utils/dateFormatters";
import BreadcrumbComponent from "@/components/BreadcrumbComponent";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
// Dialog imports kept for success dialog
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import FileInput from "@/components/FileInput";
import { FREEZE_APPLICATION_STATE_CONFIG } from "@/utils/applicationStatusConfig";
import {
  X,
  Loader2,
  Clock,
  CheckCircle2,
  Plus,
  Paperclip,
  ExternalLink,
  MessageSquare,
} from "lucide-react";

const FreezeApplication = () => {
  const [showForm, setShowForm] = useState(false);
  const [selectedApp, setSelectedApp] = useState<FreezeApplicationType | null>(null);
  const [isUploadingToApp, setIsUploadingToApp] = useState(false);

  // Edit mode state for clarification status
  const [editReason, setEditReason] = useState("");
  const [editDateRanges, setEditDateRanges] = useState("");
  const [editFiles, setEditFiles] = useState<File[]>([]);
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  const { authPhone } = useAuth();
  const updateMutation = useUpdateFreezeApplicationMutation();

  // Scroll to top when view changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [showForm, selectedApp]);

  // Initialize edit form when selecting an app with clarification status
  useEffect(() => {
    if (selectedApp?.state === "clarification") {
      setEditReason(selectedApp.reason || "");
      setEditDateRanges(selectedApp.date_ranges_text || "");
      setEditFiles([]);
    }
  }, [selectedApp]);

  const {
    formData,
    isSubmitting,
    successDialogOpen,
    setSuccessDialogOpen,
    existingApplications,
    isLoadingApplications,
    handleReasonChange,
    handleDateRangesTextChange,
    handleTicketChange,
    handleFileChange,
    handleRemoveFile,
    handleSubmit,
    handleClearApplication,
    uploadToExistingApplication,
  } = useFreezeApplication();

  const { ticketsData, isLoading: isLoadingActiveTickets } = useTickets();
  const { data: closedTicketsData, isLoading: isLoadingClosedTickets } = useGetClosedTicketsQuery();

  const isLoadingTickets = isLoadingActiveTickets || isLoadingClosedTickets;

  const hasRemainingServices = (ticket: Ticket): boolean => {
    if (ticket.type !== "package" && ticket.type !== "membership") return false;

    // Check service_list
    if (ticket.service_list && ticket.service_list.length > 0) {
      if (ticket.service_list.some((service) => service.count > 0)) return true;
    }

    // Check service_list_atm_sale (used for closed tickets)
    if (ticket.service_list_atm_sale && ticket.service_list_atm_sale.length > 0) {
      if (ticket.service_list_atm_sale.some((service) => service.count > 0)) return true;
    }

    return false;
  };

  const freezableTickets = useMemo(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const activeTickets =
      ticketsData?.filter(
        (ticket) =>
          ticket.status === "active" && (ticket.type === "package" || ticket.type === "membership")
      ) || [];

    const recentClosedTickets =
      closedTicketsData?.filter((ticket) => {
        if (ticket.type !== "package" && ticket.type !== "membership") return false;
        if (!ticket.end_date) return false;

        const endDate = new Date(ticket.end_date);
        if (endDate < sevenDaysAgo) return false;

        return hasRemainingServices(ticket);
      }) || [];

    return [...activeTickets, ...recentClosedTickets] as PackageTicket[];
  }, [ticketsData, closedTicketsData]);

  // Helper to get ticket title by ticket_id (which is item_id from 1C without dashes)
  const getTicketTitle = (ticketId?: string) => {
    if (!ticketId) return null;
    const normalizedId = ticketId.replace(/-/g, "");
    const ticket = ticketsData?.find(
      (t) => "item_id" in t && t.item_id.replace(/-/g, "") === normalizedId
    );
    return ticket?.title || null;
  };

  const handleSuccessClose = () => {
    setSuccessDialogOpen(false);
    handleClearApplication();
    setShowForm(false);
  };

  const handleBack = () => {
    setShowForm(false);
    handleClearApplication();
  };

  // Check if no freezable tickets available
  const hasNoTickets = !isLoadingTickets && freezableTickets.length === 0;

  // Form view
  if (showForm) {
    // Show message if no tickets available
    if (hasNoTickets) {
      return (
        <div className="flex flex-col p-3">
          <BreadcrumbComponent
            link="Заморозка абонемента"
            onLinkClick={handleBack}
            secondLink="Новое заявление"
          />

          <div className="mx-0 my-4 sm:m-6">
            <Card>
              <CardHeader>
                <CardTitle>Новое заявление</CardTitle>
              </CardHeader>
              <CardContent>
                <Alert>
                  <AlertDescription>
                    У вас нет активных абонементов для заморозки. Заморозить можно только
                    действующий абонемент.
                  </AlertDescription>
                </Alert>
              </CardContent>
              <CardFooter>
                <Button variant="outline" onClick={handleBack} className="w-full">
                  Назад
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col p-3">
        <BreadcrumbComponent
          link="Заморозка абонемента"
          onLinkClick={handleBack}
          secondLink="Новое заявление"
        />

        <form onSubmit={handleSubmit} className="mt-2 space-y-4">
          {/* Ticket Selection */}
          <div className="space-y-1">
            <Label htmlFor="ticket">
              Абонемент<span className="ml-1 text-destructive">*</span>
            </Label>
            <Select
              value={formData.selectedTicket?.item_id || ""}
              onValueChange={(value) => {
                const ticket = freezableTickets.find((t) => t.item_id === value);
                if (ticket) {
                  handleTicketChange({
                    item_id: ticket.item_id,
                    title: ticket.title,
                    active_date: ticket.active_date,
                    end_date: ticket.end_date,
                  });
                }
              }}
              disabled={isLoadingTickets}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={isLoadingTickets ? "Загрузка..." : "Выберите абонемент"}
                />
              </SelectTrigger>
              <SelectContent>
                {freezableTickets.map((ticket) => (
                  <SelectItem key={ticket.item_id} value={ticket.item_id}>
                    {ticket.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {formData.selectedTicket && (
              <p className="text-sm text-muted-foreground">
                {formatDateWithYear(formData.selectedTicket.active_date)}
                {formData.selectedTicket.end_date && (
                  <> — {formatDateWithYear(formData.selectedTicket.end_date)}</>
                )}
              </p>
            )}
          </div>

          {/* Reason */}
          <div className="space-y-1">
            <Label htmlFor="reason">
              Причина заморозки<span className="ml-1 text-destructive">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Опишите причину"
              value={formData.reason}
              onChange={(e) => handleReasonChange(e.target.value)}
              rows={2}
            />
          </div>

          {/* Date Ranges */}
          <div className="space-y-1">
            <Label htmlFor="dateRanges">
              Сроки заморозки<span className="ml-1 text-destructive">*</span>
            </Label>
            <Textarea
              id="dateRanges"
              placeholder="Например: с 01.01.2025 по 15.01.2025"
              value={formData.dateRangesText}
              onChange={(e) => handleDateRangesTextChange(e.target.value)}
              rows={1}
            />
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>
              Документы<span className="ml-1 text-destructive">*</span>
            </Label>
            <p className="text-xs text-muted-foreground">
              Прикрепите справку или документ, подтверждающий причину
            </p>
            <FileInput onChange={(e) => handleFileChange(e.target.files)} multiple />
            {formData.files.length > 0 && (
              <div className="space-y-1">
                {formData.files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 rounded border bg-muted/50 p-2"
                  >
                    <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveFile(index)}
                      className="h-6 w-6 shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3 pt-2">
            <Button
              type="submit"
              className="w-full"
              disabled={
                !formData.reason.trim() ||
                !formData.dateRangesText.trim() ||
                !formData.selectedTicket ||
                formData.files.length === 0 ||
                isSubmitting
              }
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Отправка...
                </>
              ) : (
                "Отправить заявление"
              )}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              После отправки заявления с вами свяжется менеджер для уточнения деталей
            </p>
          </div>
        </form>

        {/* Success Dialog */}
        <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <div className="mx-auto mb-4 rounded-full bg-green-100 p-3">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
              <DialogTitle className="text-center">Заявление отправлено</DialogTitle>
              <DialogDescription className="text-center">
                Ваше заявление успешно отправлено. Мы рассмотрим его в ближайшее время и свяжемся с
                вами для уточнения деталей.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={handleSuccessClose} className="w-full">
                Понятно
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Handle edit submission for clarification status
  const handleEditSubmit = async () => {
    if (!selectedApp || !authPhone) return;

    setIsSubmittingEdit(true);
    try {
      // Upload new files if any
      let newDocumentIds: number[] = [];
      if (editFiles.length > 0) {
        const uploadPromises = editFiles.map((file) => uploadAttachment(authPhone, file));
        const uploadResponses = await Promise.all(uploadPromises);
        newDocumentIds = uploadResponses.map((response) => response.data.data.id);
      }

      // Combine existing and new document IDs
      const existingIds = selectedApp.documents?.map((d) => d.id) || [];
      const allDocumentIds = [...existingIds, ...newDocumentIds];

      // Update application with new data and change status to in_progress
      await updateMutation.mutateAsync({
        phone: authPhone,
        applicationId: selectedApp.id,
        data: {
          reason: editReason,
          date_ranges_text: editDateRanges,
          documents: allDocumentIds,
          state: "in_work",
        },
      });

      // Go back to list
      setSelectedApp(null);
    } catch {
      // Error is handled by mutation
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  // Detail view for selected application
  if (selectedApp) {
    const config =
      FREEZE_APPLICATION_STATE_CONFIG[selectedApp.state] || FREEZE_APPLICATION_STATE_CONFIG.new;
    const fullTitle =
      selectedApp.ticket_title || getTicketTitle(selectedApp.ticket_id) || "Абонемент";
    const isClarification = selectedApp.state === "clarification";

    return (
      <div className="flex flex-col p-3">
        <BreadcrumbComponent
          link="Заморозка абонемента"
          onLinkClick={() => setSelectedApp(null)}
          secondLink="Детали заявления"
        />

        <div className="mt-2 space-y-4">
          <div className="flex items-center justify-between">
            <Badge variant={config.variant}>{config.label}</Badge>
            <span className="text-sm text-muted-foreground">
              {formatDateShort(selectedApp.createdAt)}
            </span>
          </div>

          {/* Manager comment - shown only for clarification status */}
          {isClarification && selectedApp.manager_comment && (
            <div className="space-y-2 rounded-lg border border-destructive/50 bg-destructive/5 p-4">
              <div className="flex items-center gap-2 text-destructive">
                <MessageSquare className="h-4 w-4" />
                <Label className="font-medium">Комментарий менеджера</Label>
              </div>
              <p className="text-sm">{selectedApp.manager_comment}</p>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-muted-foreground">Абонемент</Label>
            <p className="font-medium">{fullTitle}</p>
          </div>

          {/* Reason - editable for clarification */}
          <div className="space-y-1">
            <Label className="text-muted-foreground">Причина</Label>
            {isClarification ? (
              <Textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                rows={2}
              />
            ) : (
              <p>{selectedApp.reason}</p>
            )}
          </div>

          {/* Date ranges - editable for clarification */}
          <div className="space-y-1">
            <Label className="text-muted-foreground">Сроки заморозки</Label>
            {isClarification ? (
              <Textarea
                value={editDateRanges}
                onChange={(e) => setEditDateRanges(e.target.value)}
                rows={1}
              />
            ) : (
              <p>{selectedApp.date_ranges_text || "—"}</p>
            )}
          </div>

          <div className="space-y-2 pt-2">
            <Label className="text-muted-foreground">Документы</Label>
            {selectedApp.documents && selectedApp.documents.length > 0 ? (
              <div className="space-y-2">
                {selectedApp.documents.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded border p-2 transition-colors hover:bg-muted/50"
                  >
                    <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate text-sm">{doc.title || doc.filename}</span>
                    <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Не прикреплены</p>
            )}

            {/* File upload for clarification status */}
            {isClarification && (
              <div className="space-y-2 pt-2">
                <FileInput
                  onChange={(e) => {
                    if (e.target.files) {
                      setEditFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
                    }
                  }}
                  multiple
                  disabled={isSubmittingEdit}
                />
                {editFiles.length > 0 && (
                  <div className="space-y-1">
                    {editFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 rounded border bg-muted/50 p-2"
                      >
                        <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => setEditFiles((prev) => prev.filter((_, i) => i !== index))}
                          className="h-6 w-6 shrink-0"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* File upload for new status */}
            {selectedApp.state === "new" && (
              <div className="pt-2">
                <FileInput
                  onChange={async (e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setIsUploadingToApp(true);
                      const existingIds = selectedApp.documents?.map((d) => d.id) || [];
                      const success = await uploadToExistingApplication(
                        selectedApp.id,
                        existingIds,
                        Array.from(e.target.files)
                      );
                      setIsUploadingToApp(false);
                      if (success) {
                        setSelectedApp(null);
                      }
                    }
                  }}
                  multiple
                  disabled={isUploadingToApp}
                />
                {isUploadingToApp && (
                  <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Загрузка...
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Submit button for clarification */}
          {isClarification && (
            <div className="pt-4">
              <Button
                onClick={handleEditSubmit}
                className="w-full"
                disabled={!editReason.trim() || !editDateRanges.trim() || isSubmittingEdit}
              >
                {isSubmittingEdit ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Отправка...
                  </>
                ) : (
                  "Отправить ответ"
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main view with history
  return (
    <div className="flex flex-col p-3">
      <BreadcrumbComponent link="Заморозка абонемента" />

      <div className="mx-0 my-4 space-y-6 sm:m-6">
        {/* Header with action button */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold">
                Заморозка и продление срока действия абонемента
              </h1>
              <Badge className="bg-yellow-400 text-xs text-yellow-900 hover:bg-yellow-400">
                Бета
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Приостановка действия и продление сроков абонемента производится на основании
              медицинской справки
            </p>
          </div>
          <Button onClick={() => setShowForm(true)} className="w-full gap-2 sm:w-auto">
            <Plus className="h-4 w-4" />
            Новое заявление
          </Button>
        </div>

        {/* Applications list */}
        {isLoadingApplications ? (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center gap-3">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-muted-foreground">Загрузка заявлений...</p>
              </div>
            </CardContent>
          </Card>
        ) : existingApplications && existingApplications.length > 0 ? (
          <div className="space-y-4">
            <h2 className="text-lg font-medium">Мои заявления ({existingApplications.length})</h2>
            {existingApplications.map((app) => {
              const config =
                FREEZE_APPLICATION_STATE_CONFIG[app.state] || FREEZE_APPLICATION_STATE_CONFIG.new;

              // Extract ticket name without dates for display
              const fullTitle = app.ticket_title || getTicketTitle(app.ticket_id) || "Абонемент";
              const ticketName = fullTitle.replace(/\s*\([^)]*\)\s*$/, "");

              return (
                <Card key={app.id} className="cursor-pointer" onClick={() => setSelectedApp(app)}>
                  <CardContent className="space-y-2 p-3">
                    <Badge variant={config.variant} className="text-xs">
                      {config.label}
                    </Badge>

                    <h3 className="text-sm font-medium leading-snug">{ticketName}</h3>

                    {app.date_ranges_text && (
                      <p className="text-sm">
                        <span className="text-muted-foreground">Сроки: </span>
                        {app.date_ranges_text}
                      </p>
                    )}

                    <div className="flex items-center justify-between pt-1 text-xs text-muted-foreground">
                      <span>{formatDateShort(app.createdAt)}</span>
                      {app.documents && app.documents.length > 0 && (
                        <span className="flex items-center gap-1">
                          <Paperclip className="h-3 w-3" />
                          {app.documents.length}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="flex flex-col items-center justify-center gap-3 text-center">
                <div className="rounded-full bg-muted p-3">
                  <Clock className="h-8 w-8 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium">Заявлений пока нет</p>
                  <p className="text-sm text-muted-foreground">
                    Нажмите кнопку выше, чтобы подать заявление на заморозку
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default FreezeApplication;
